import { HelpLinks } from '@core/models';
import { tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConfigModel } from '@models/app/app-config.model';
import { Injectable, Inject } from '@angular/core';

export type CompanyName = 'CREDEM' | 'AVVERA';

export type ProductName = 'CAPTIVE' | 'PROSPECT';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private companyCode: string;

  private productCode: string;

  private res: string;

  constructor(
    @Inject('APP_CONFIG') private appConfig: AppConfigModel,
    private http: HttpClient
  ) {}

  /**
   * inizializza le proprietà specifiche della banca
   */
  init(props: { companyCode: string; productCode: string }): Observable<void> {
    this.companyCode = props.companyCode;
    this.productCode = props.productCode;

    return this.loadResJson().pipe(
      tap(() => {
        this.updateLogo();
        this.updateFavicon();
        this.updateHelpLinks();
        this.updateLabelsFilter();
        this.updateThemeColor();
        this.updateSrcAlias();
        this.updateIdle();
      })
    );
  }

  /**
   * restituisce i dati della collection richiesta
   */
  getResCollection<T>(collectionName): T[] {
    let collection = [];

    try {
      collection = JSON.parse(this.res)[collectionName];
    } catch (_) {}

    return collection;
  }

  private updateLogo(): void {
    if (this.isCompany('AVVERA')) {
      this.appConfig.theme.logoUrl = 'assets/icons/logo_banca_avvera.jpg';
    } else if (this.isCompany('CREDEM')) {
      this.appConfig.theme.logoUrl = 'assets/icons/logo_banca_credem.jpg';
    } else {
      this.appConfig.theme.logoOnHeader = false;
    }
  }

  private updateFavicon(): void {
    const favicon: HTMLLinkElement = document.querySelector('link[rel="icon"]');

    if (favicon && this.isCompany('AVVERA')) {
      favicon.href = 'assets/icon/favicon_avvera.ico';
    } else if (favicon && this.isCompany('CREDEM')) {
      favicon.href = 'assets/icon/favicon_credem.ico';
    }
  }

  private updateLabelsFilter(): void {
    if (this.isCompany('AVVERA') || this.isCompany('CREDEM')) {
      const companyName = this.getCompanyShortName();
      const productCode = this.getProductCodeShort();

      this.appConfig.labels.filter = {
        key: 'bc',
        value: `${companyName}-${productCode}`
      };
    } else {
      this.appConfig.labels.filter = undefined;
    }
  }

  private updateThemeColor(): void {
    const themeColor: HTMLMetaElement = document.querySelector(
      'meta[name="theme-color"]'
    );

    if (themeColor && this.isCompany('AVVERA')) {
      themeColor.content = '#ab5998';
    } else if (themeColor && this.isCompany('CREDEM')) {
      themeColor.content = '#20623b';
    }
  }

  private updateSrcAlias(): void {
    if (this.isCompany('AVVERA')) {
      this.appConfig.theme.srcAlias['./assets/images/information.svg'] =
        './assets/images/information_avvera.svg';
      this.appConfig.theme.srcAlias['./assets/images/instalment_tip.svg'] =
        './assets/images/instalment_tip_avvera.svg';
      this.appConfig.theme.srcAlias['./assets/icons/question.svg'] =
        './assets/icons/question_avvera.svg';
      this.appConfig.theme.srcAlias['./assets/icons/pen.svg'] =
        './assets/icons/pen_avvera.svg';
      this.appConfig.theme.srcAlias['./assets/icons/helpline.svg'] = undefined;
      this.appConfig.theme.srcAlias['./assets/icons/live_chat.svg'] = undefined;
    } else if (this.isCompany('CREDEM')) {
      this.appConfig.theme.srcAlias['./assets/images/information.svg'] =
        './assets/images/information_credem.svg';
      this.appConfig.theme.srcAlias['./assets/images/instalment_tip.svg'] =
        './assets/images/instalment_tip_credem.svg';
      this.appConfig.theme.srcAlias['./assets/icons/question.svg'] =
        './assets/icons/question_credem.svg';
      this.appConfig.theme.srcAlias['./assets/icons/pen.svg'] =
        './assets/icons/pen_credem.svg';
      this.appConfig.theme.srcAlias['./assets/icons/helpline.svg'] =
        './assets/icons/helpline_cre.svg';
      this.appConfig.theme.srcAlias['./assets/icons/live_chat.svg'] =
        './assets/icons/live_chat_cre.svg';
    }
  }

  private updateHelpLinks(): void {
    const helpLinks = this.getResCollection<{ key: HelpLinks; url: string }>(
      'helpLinks'
    );

    this.appConfig.generic.helpLinks = helpLinks.map(item => [
      item.key,
      item.url
    ]);
  }

  private updateIdle(): void {
    if (this.isProduct('CAPTIVE')) {
      this.appConfig.generic.notifyIdle = true;
      this.appConfig.generic.idleTimeout = 300;
    } else if (this.isProduct('PROSPECT')) {
      this.appConfig.generic.notifyIdle = true;
      this.appConfig.generic.idleTimeout = 1200;
    } else {
      this.appConfig.generic.notifyIdle = false;
    }
  }

  private isCompany(name: CompanyName): boolean {
    return this.getCompanyName() === name;
  }

  private isProduct(name: ProductName): boolean {
    return this.productCode && this.productCode.includes(name);
  }

  private getCompanyShortName(): string {
    const name = this.getCompanyName();

    return name && name.substring(0, 3);
  }

  private getCompanyName(): CompanyName {
    if (this.companyCode === '041') {
      return 'AVVERA';
    } else if (this.companyCode === '001') {
      return 'CREDEM';
    }
  }

  private getProductCodeShort(): string {
    if (typeof this.productCode !== 'string') {
      return '';
    } else {
      return this.productCode.substring(0, 3);
    }
  }

  private loadResJson(): Observable<any> {
    const shortName = this.getCompanyShortName();
    const code = shortName ? shortName.toLowerCase() : '';

    return this.http
      .get(`assets/res/res-${code}.json`)
      .pipe(tap(res => (this.res = JSON.stringify(res))));
  }
}
